/** Single source of truth for how the client presents game modes.
 *
 *  The play menu and the room lobby both show a mode picker, and they used to
 *  each carry their own private copy of the mode list, the button labels and
 *  the announcer-voice map. They drifted: `burnout` was missing from
 *  lobbyScreen's voice map, so picking Burnout in a room announced nothing.
 *  Adding a mode is now a one-line change here. */

/** Mode id → button label, in the order both mode pickers display them. */
export const MODE_ORDER = [
  ["gungame", "Gun Game"],
  ["descent", "🔻 Descent"],
  ["purge", "🧟 Purge"],
  ["ffa", "FFA"],
  ["arena", "🛸 Arena"],
  ["treasure", "💰 Treasure"],
  ["burnout", "🔥 Burnout"],
  ["plunder", "🚩 Flags"],
  ["relay", "⚡ Relay"],
  ["onegun", "🔫 One Gun"],
  ["horde", "💀 Horde"],
  ["royale", "👑 Royale"],
] as const;

/** Every mode id, in picker order. */
export const MODE_IDS: readonly string[] = MODE_ORDER.map(([id]) => id);

/** Mode id → announcer voice line. FFA still borrows "deathmatch"; every other
 *  mode has a dedicated recorded line. */
const MODE_VOICE: Record<string, string> = {
  gungame: "gungame",
  descent: "gungame", // reverse Gun Game — reuses the Gun Game announcer line
  purge: "purge",
  ffa: "deathmatch",
  arena: "arena",
  treasure: "treasure",
  burnout: "burnout",
  plunder: "flags",
  relay: "relay",
  onegun: "onegun",
  horde: "horde",
  royale: "royale",
};

/** The line the announcer reads for `mode`. Note `voice()` plays nothing for an
 *  id with no recorded `voice_<id>` sample (it does NOT fall back to TTS), so
 *  every mode in MODE_ORDER must map to a real recorded line above. */
export const modeVoice = (mode: string): string => MODE_VOICE[mode] ?? mode;

/** Round-time picker options: `[seconds-as-string, label]`, longest-lived last.
 *  `"0"` means no timer. The menu, the room lobby and the couch lobby all show
 *  a round-time <select>; they each carried a private copy of this list, so it
 *  lives here now for the same reason MODE_ORDER does. */
export const ROUND_TIMES: readonly (readonly [string, string])[] = [
  ["180", "3 min"], ["300", "5 min"], ["600", "10 min"], ["900", "15 min"],
  ["0", "No timer (until someone wins)"],
];

/** Populate a round-time <select> from ROUND_TIMES and select `selected`
 *  (default 10 min — the value every picker already defaulted to). */
export function fillRoundSelect(sel: HTMLSelectElement, selected = "600"): void {
  for (const [value, text] of ROUND_TIMES) sel.append(new Option(text, value));
  sel.value = selected;
}

/** Bot difficulty option: stable server id + its Title-Case picker label. */
export interface DiffOption { id: string; label: string }

/** The bot-difficulty ladder, easiest first. Every lobby offers the SAME four —
 *  the couch (local split-screen) lobby used to omit "V. Easy" and show raw
 *  lowercase ids, so pull it here so the three pickers can't drift again. */
export const DIFFICULTIES: readonly DiffOption[] = [
  { id: "veryeasy", label: "V. Easy" }, { id: "easy", label: "Easy" },
  { id: "medium", label: "Medium" }, { id: "hard", label: "Hard" },
];

/** Team text-colour hexes shared by every menu/lobby that labels the two teams.
 *  `red`/`blue` were duplicated verbatim across the play menu and the room lobby
 *  team pickers; pull them here so the two team colours can't drift. `none` is
 *  the neutral (unassigned) label colour used in the rosters. */
export const TEAM_COLOR = { red: "#ff5577", blue: "#5aa9ff", none: "#8a95a5" } as const;

/** `team` (1 = red, 2 = blue, anything else = unassigned) → its label colour. */
export const teamColor = (team: number | undefined): string =>
  team === 1 ? TEAM_COLOR.red : team === 2 ? TEAM_COLOR.blue : TEAM_COLOR.none;

/** Short one-liner for each game mode — shown under the mode selector in the
 *  play menu AND the room lobby so new players know what they're picking. */
export const MODE_DESC: Record<string, string> = {
  gungame: "Every kill with your LADDER gun promotes you to the next weapon. Power-up guns and explosions kill but don't count toward the ladder. Get knifed and you drop one. First to the final knife tier wins.",
  descent: "Reverse Gun Game: you SPAWN with the strongest gun and every kill hands you a WEAKER one, sliding down the power curve. Leading is a handicap — the pack you're beating is better armed. Ride it all the way down to the final knife and land the kill to win.",
  purge: "Zombie survival with endless undead rounds. Your default weapon SWITCHES every round. Earn points, buy weapons & perks, breach sectors. Revive teammates (or solo: last alive wins).",
  ffa: "Pure deathmatch: everyone for themselves, every weapon on the map. Most kills when the timer ends wins.",
  treasure: "Capture the Treasure: one chest, everyone wants it. Carry it to bank hold-time — 30 seconds of holding wins. Die and you drop it where you fall, so the carrier is the target.",
  arena: "Bob-omb Blast kart battle: grab bombs from the item boxes and blast rival karts — +1 point per hit, −1 when you are hit. First to 3 points wins (4 with 3+ karts).",
  plunder: "Team capture-the-flag. Defend your colored base, steal the enemy flag, and carry it into your team's glowing capture circle. Players always respawn in their team zone. First team to 3 captures wins.",
  burnout: "Race up the weapon ladder: the clock promotes everyone every 18s and every kill advances you instantly. Three kills without dying freezes your timer for 10s. Win with the final Burnout Knife before its 12s fuse expires. Knife deaths and fuse-outs cost 3 tiers. In the final minute, heat rises every 10s. Feeling bold? STOKE (G) lights your own fuse early — a kill within 12s pays +2 bonus rungs, but letting it expire burns you out.",
  relay: "IMPERION RELAY: one beam rifle, always armed. Claim the chest and every beam kill scores — kill the holder with anything to take the Imperion for yourself. First to 5 beams wins.",
  onegun: "There is ONE gun. Everyone else gets throwing knives. Every kill the holder lands promotes THE GUN up the ladder — kill the holder and it drops where they fall, tier kept. The final-rung gun kill wins the match.",
  horde: "HORDEBEARER: deathmatch with a roaming zombie pack. Claim the cursed SKULL and the horde fights FOR you — it spares you, hunts whoever you walk at, and its kills count as yours. Kill the bearer to drop the skull. First to 30 frags wins (zombies score nothing — hunt players).",
  royale: "BATTLE ROYALE: pure deathmatch, everyone starts with the BULWARK LMG (weapon chest for variety). TWO LIVES each — burn both and you're out, spectating. Last one standing wins.",
};

/** MODE CARD (shown for ~6 s when a match starts): three SHORT lines — the
 *  goal, how you win, one tip. MODE_DESC is the full paragraph for the picker;
 *  this is what a player can actually read while spawning. Keep every line
 *  under ~60 characters. */
export const MODE_QUICK: Record<string, { icon: string; name: string; goal: string; win: string; tip: string }> = {
  gungame: { icon: "🔫", name: "GUN GAME", goal: "Every kill gives you the NEXT gun on the ladder", win: "First to land a kill with the final KNIFE wins", tip: "Getting knifed sends you back one gun" },
  descent: { icon: "🔻", name: "DESCENT", goal: "You start with the BEST gun — each kill gives a weaker one", win: "Ride it down and win with the final KNIFE kill", tip: "The leader is the worst armed — punish them" },
  purge: { icon: "🧟", name: "PURGE", goal: "Survive endless zombie rounds with your squad", win: "Last as many rounds as you can", tip: "Hits pay points — spend them at the gold $ shops" },
  ffa: { icon: "💥", name: "FREE FOR ALL", goal: "Everyone for themselves, every weapon allowed", win: "Most kills when the timer ends", tip: "Grab chests for power weapons" },
  arena: { icon: "🛸", name: "ARENA", goal: "Kart battle — grab bombs from the item boxes", win: "+1 per hit on a rival, first to 3 points", tip: "Getting hit costs you a point" },
  treasure: { icon: "💰", name: "TREASURE", goal: "Grab the treasure chest and HOLD it", win: "First to 30 seconds of holding wins", tip: "The carrier is everyone's target — keep moving" },
  burnout: { icon: "🔥", name: "BURNOUT", goal: "The clock promotes everyone; kills promote you faster", win: "Win with a kill using the final Burnout Knife", tip: "3 kills without dying freezes your timer" },
  plunder: { icon: "🚩", name: "FLAGS", goal: "Steal the enemy flag, bring it to YOUR circle", win: "First team to 3 captures", tip: "Kill the flag carrier to return your flag" },
  relay: { icon: "⚡", name: "RELAY", goal: "Claim the IMPERION beam rifle — beam kills score", win: "First to 5 beam kills", tip: "Kill the holder with anything to steal it" },
  onegun: { icon: "🔫", name: "ONE GUN", goal: "There is ONE gun — everyone else has knives", win: "Finish the gun's ladder while holding it", tip: "Kill the holder and grab the gun they drop" },
  horde: { icon: "💀", name: "HORDEBEARER", goal: "Claim the SKULL and the zombie horde fights for you", win: "Most kills — the horde's kills count as yours", tip: "Kill the bearer to take the skull" },
  royale: { icon: "👑", name: "ROYALE", goal: "Everyone gets 2 lives", win: "Last one standing wins", tip: "Pick your fights — you only get two chances" },
};
